import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { UIEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AISystemsOverview } from '../components/features/AISystemsOverview/AISystemsOverview';
import { Toast } from '../components/ui/Toast/Toast';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDispatch } from 'react-redux';
import { resetStepper } from '../store/slices/stepperSlice';
import { useAppSelector } from '../hooks/redux';
import {
  setAISystemStatus,
  setListScrollTop
} from '../store/slices/aiSystemsSlice';
import { useAccountDefault } from '../components/hooks/useAccountDefault';
import { AddAISystemAction } from '../components/features/AddAISystemAction/AddAISystemAction';
import { showToast as showGlobalToast } from '../store/slices/toastSlice';

export function AISystemsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollSaveFrameRef = useRef<number | null>(null);
  const scrollRestoreFrameRef = useRef<number | null>(null);
  const latestScrollTopRef = useRef(0);
  const hasRestoredScrollRef = useRef(false);
  const isRestoringScrollRef = useRef(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const { aiSystemStatus, listScrollTop, systems } = useAppSelector(
    (state) => state.aiSystems
  );
  const { account } = useAppSelector((state) => state.accountDefault);
  const { fetchMyAccount } = useAccountDefault();

  const hasNoPlan = account?.subscription?.plan === 'NO_PLAN';

  useEffect(() => {
    if (!account?.subscription?.plan) {
      fetchMyAccount();
    }
  }, [account, fetchMyAccount]);

  useEffect(() => {
    if (location.state?.showDeletedToast) {
      setShowToast(true);
      setToastMessage('AI System successfully deleted!');
      // Clear the state so toast doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }

    if (location.state?.resetStepper) {
      dispatch(resetStepper());
      window.history.replaceState({}, document.title);
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const callbackError = searchParams.get('error');

    if (!callbackError) return;

    dispatch(showGlobalToast({ message: callbackError, type: 'error' }));

    searchParams.delete('error');
    const nextSearch = searchParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, dispatch]);

  useEffect(() => {
    if (aiSystemStatus === null) return;

    if (aiSystemStatus === 'draft') setToastMessage('AI System draft created!');
    if (aiSystemStatus === 'created')
      setToastMessage('AI System successfully added!');

    setShowToast(true);
    dispatch(setAISystemStatus(null));
  }, [aiSystemStatus, dispatch]);

  useEffect(() => {
    return () => {
      if (scrollSaveFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSaveFrameRef.current);
      }
      if (scrollRestoreFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollRestoreFrameRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current || listScrollTop <= 0) return;
    if (systems.length === 0) return;

    let attempts = 0;
    const maxAttempts = 20;

    const restoreScroll = () => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      const maxScrollTop = Math.max(
        0,
        scrollContainer.scrollHeight - scrollContainer.clientHeight
      );
      const nextScrollTop = Math.min(listScrollTop, maxScrollTop);

      isRestoringScrollRef.current = true;
      scrollContainer.scrollTo({ top: nextScrollTop, behavior: 'auto' });

      window.requestAnimationFrame(() => {
        isRestoringScrollRef.current = false;
      });

      if (maxScrollTop >= listScrollTop) {
        hasRestoredScrollRef.current = true;
        return;
      }

      if (attempts >= maxAttempts) return;

      attempts += 1;
      scrollRestoreFrameRef.current =
        window.requestAnimationFrame(restoreScroll);
    };

    scrollRestoreFrameRef.current = window.requestAnimationFrame(restoreScroll);

    return () => {
      if (scrollRestoreFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollRestoreFrameRef.current);
        scrollRestoreFrameRef.current = null;
      }
    };
  }, [listScrollTop, systems.length]);

  const handleDashboardScroll = (event: UIEvent<HTMLDivElement>) => {
    latestScrollTopRef.current = event.currentTarget.scrollTop;

    if (isRestoringScrollRef.current) return;
    if (
      !hasRestoredScrollRef.current &&
      listScrollTop > 0 &&
      latestScrollTopRef.current === 0
    ) {
      return;
    }

    if (scrollSaveFrameRef.current !== null) return;

    scrollSaveFrameRef.current = window.requestAnimationFrame(() => {
      dispatch(setListScrollTop(latestScrollTopRef.current));
      scrollSaveFrameRef.current = null;
    });
  };

  return (
    <AppLayout>
      <DashboardLayout
        title="AI Systems"
        addButtonAction={<AddAISystemAction disabled={hasNoPlan} />}
        scrollContainerRef={scrollContainerRef}
        onScroll={handleDashboardScroll}
      >
        <AISystemsOverview />
      </DashboardLayout>

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        text={toastMessage}
        type="success"
      />
    </AppLayout>
  );
}
